import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { assignViewerColorIndices } from '../utils/get-viewer-color-index';
import type { SolutionUserPresenceItem } from '../xstate/prostudio.xstate.types';

export function getPresenceDisplayName(
  presence: SolutionUserPresenceItem,
  projectUsers:
    | { id?: unknown; firstName?: string | null; lastName?: string | null }[]
    | null
    | undefined
): string {
  if (presence.user) {
    const n = [presence.user.firstName, presence.user.lastName]
      .filter(Boolean)
      .join(' ');
    if (n) return n;
  }
  const found = projectUsers?.find((pu) =>
    pu?.id ? String(pu.id) === String(presence.userId) : false
  );
  if (found) {
    const n = [found.firstName, found.lastName].filter(Boolean).join(' ');
    if (n) return n;
  }
  return 'Viewing';
}

export function useRowCoworkerFocus(
  rowId: string,
  pathname: string
): Map<string, { label: string; colorIndex: number }> {
  const solutionUserPresences = useProStudioCtxSelector(
    (ctx) => ctx.solutionUserPresences
  );
  const userInfo = useProStudioCtxSelector((ctx) => ctx.userInfo);
  const projectUsers = useProStudioCtxSelector((ctx) => ctx.projectUsers);

  return useMemo(() => {
    const map = new Map<string, { label: string; colorIndex: number }>();
    if (!solutionUserPresences?.length) return map;

    const currentUserId = String(userInfo?.id ?? '');

    const activeOthers = sortBy(
      solutionUserPresences.filter(
        (p) =>
          p.isActive && String(p.userId ?? p.user?.id ?? '') !== currentUserId
      ),
      (p) => {
        const first = p.user?.firstName ?? '';
        const last = p.user?.lastName ?? '';
        const name = [first, last].filter(Boolean).join(' ').toLowerCase();
        return name || String(p.userId ?? p.id ?? '');
      }
    );

    const colorIndexMap = assignViewerColorIndices(activeOthers);

    activeOthers
      .filter(
        (p) =>
          p.pagePath === pathname && p.pageParams?.focusedCell?.rowId === rowId
      )
      .forEach((p) => {
        const fc = p.pageParams!.focusedCell!;
        const name = getPresenceDisplayName(p, projectUsers);
        const userId = String(p.userId ?? p.user?.id ?? '');
        const colorIndex = colorIndexMap.get(userId) ?? 0;
        const existing = map.get(fc.columnId);
        map.set(fc.columnId, {
          label: existing ? `${existing.label}, ${name}` : name,
          colorIndex: existing?.colorIndex ?? colorIndex,
        });
      });

    return map;
  }, [solutionUserPresences, userInfo, projectUsers, rowId, pathname]);
}
